import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { plata, fecha } from '../utils/formato';

// La primera mirada del día.
//
// Arriba, y antes que cualquier cifra, las dos cosas que cuestan plata si se
// pasan por alto: quién no ha pagado y qué contrato se vence. Un total de
// cartera no dice a quién llamar, y una fecha de vencimiento que se pasa no se
// recupera.
function Panel({ irA }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    axios.get('/api/panel')
      .then(r => { if (vivo) setD(r.data); })
      .catch(() => { if (vivo) setError('No se pudo cargar el panel'); });
    return () => { vivo = false; };
  }, []);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!d) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const Dato = ({ rotulo, valor, pie, color }) => (
    <div className="dato" style={{ '--color': color }}>
      <div className="rotulo">{rotulo}</div>
      <div className="valor cifra">{valor}</div>
      {pie && <div className="pie">{pie}</div>}
    </div>
  );

  const costoVacios = d.vacios.reduce((s, v) => s + (v.costo_mensual || 0), 0);

  return (
    <>
      <div className="encabezado">
        <h1>Panel</h1>
        <p>Lo que hay que atender hoy</p>
      </div>

      {d.en_mora.length > 0 && (
        <div className="aviso malo">
          <strong>
            {d.total_en_mora === 1
              ? `${d.en_mora[0].arrendatario} debe ${plata(d.en_mora[0].saldo)}`
              : `${d.total_en_mora} arrendatarios en mora`}
          </strong>
          {' — '}
          {d.en_mora.slice(0, 3).map(m =>
            `${m.inmueble} (${m.meses} ${m.meses === 1 ? 'mes' : 'meses'})`).join(', ')}
          {d.total_en_mora > 3 && ` y ${d.total_en_mora - 3} más`}.
          <button className="boton chico" style={{ marginLeft: 10 }}
                  onClick={() => irA('cobrar')}>
            A quién cobrar
          </button>
        </div>
      )}

      {d.por_vencer.length > 0 && (
        <div className="aviso ojo">
          <strong>
            {d.por_vencer.length === 1 ? 'Hay 1 contrato por vencerse'
                                       : `Hay ${d.por_vencer.length} contratos por vencerse`}
          </strong>
          {' — '}
          {d.por_vencer.slice(0, 3).map(c =>
            `${c.inmueble} en ${c.dias} ${c.dias === 1 ? 'día' : 'días'}`).join(', ')}.
          <button className="boton chico" style={{ marginLeft: 10 }}
                  onClick={() => irA('contratos')}>
            Ver cuáles
          </button>
        </div>
      )}

      <div className="rejilla">
        <Dato rotulo="🏢 Inmuebles" valor={d.inmuebles}
              pie={`${d.arrendados} arrendados · ${d.vacios.length} vacíos`}
              color="var(--acero)" />
        <Dato rotulo="📅 Debería entrar este mes" valor={plata(d.esperado_mes)}
              pie="si todos pagan" color="var(--venta)" />
        <Dato rotulo="📥 Por cobrar" valor={plata(d.por_cobrar)}
              pie={d.total_en_mora > 0
                ? `${d.total_en_mora} en mora` : 'nadie en mora'}
              color={d.por_cobrar > 0 ? 'var(--ojo)' : 'var(--bueno)'} />
        <Dato rotulo="🕳️ Vacíos le cuestan" valor={plata(costoVacios)}
              pie="al mes, sin producir nada"
              color={costoVacios > 0 ? 'var(--malo)' : 'var(--bueno)'} />
      </div>

      {d.vacios.length > 0 && (
        <div className="bloque">
          <h2>🕳️ Sin arrendar <span className="contador">{d.vacios.length}</span></h2>
          <div className="cuerpo" style={{ paddingBottom: 0 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
              Un inmueble vacío no produce «cero»: sigue costando predial y
              administración todos los meses.
            </p>
          </div>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Inmueble</th>
                  <th>Tipo</th>
                  <th style={{ textAlign: 'right' }}>Le cuesta al mes</th>
                </tr>
              </thead>
              <tbody>
                {d.vacios.map(v => (
                  <tr key={v.id}>
                    <td className="recio">{v.nombre}</td>
                    <td><span className="pinta gris">{v.tipo}</span></td>
                    <td className="num recio">{plata(v.costo_mensual)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {d.por_vencer.length > 0 && (
        <div className="bloque">
          <h2>📅 Contratos por vencerse</h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Inmueble</th>
                  <th>Arrendatario</th>
                  <th>Se vence</th>
                </tr>
              </thead>
              <tbody>
                {d.por_vencer.map(c => (
                  <tr key={c.id}>
                    <td className="recio">{c.inmueble}</td>
                    <td>{c.arrendatario}</td>
                    <td>
                      <span className={'pinta ' + (c.dias <= 30 ? 'malo' : 'ojo')}>
                        {c.dias < 0 ? `vencido hace ${-c.dias} días`
                          : `en ${c.dias} ${c.dias === 1 ? 'día' : 'días'}`}
                      </span>
                      <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                        {fecha(c.fecha_fin)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cuerpo" style={{ paddingTop: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
              En locales comerciales las fechas tienen consecuencias legales. El
              programa avisa; los términos del contrato son cosa de su abogado.
            </p>
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>⚡ Qué quieres hacer</h2>
        <div className="cuerpo">
          <div style={{ display: 'grid', gap: 10,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {[
              ['cobrar',      '📥', 'Registrar un pago', 'var(--venta)'],
              ['contratos',   '📄', 'Nuevo contrato',    'var(--acero)'],
              ['gastos',      '🔧', 'Anotar un arreglo', 'var(--malo)'],
              ['rendimiento', '💰', 'Ver qué rinde',     'var(--obra)'],
            ].map(([id, ico, texto, color]) => (
              <button key={id} onClick={() => irA(id)}
                      style={{ padding: '16px 14px', cursor: 'pointer', font: 'inherit',
                               borderRadius: 'var(--radio)', border: '1px solid var(--raya)',
                               background: 'var(--tarjeta)', textAlign: 'left' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{ico}</div>
                <div style={{ fontWeight: 700, color }}>{texto}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Panel;
